import React, { useEffect, useRef, useState } from 'react';
import { each } from 'lodash-es';
import dayjs from 'util/date';

import AutocompleteFieldConfig from 'components/Explorer/models/autocomplete_field_config';
import * as QueryParams from 'components/Explorer/models/query_params';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';
import FilterSet from 'components/Explorer/models/filter_set';
import { CountriesField, DateField } from 'components/Explorer/Form';
import config from 'util/site_config';

import MentionSourcesFilter from '../MentionSourcesFilter';

const buildTypeOptions = () => {
  const options = {};
  each(
    config.mentionSourcesTypes,
    (type) =>
      (options[type] = I18n.t(type, {
        scope: 'Explorer.MentionSources.FilterPanel.type',
        default: type
      }))
  );
  return options;
};

const buildAutocompleteFieldConfig = () => {
  const acf = new AutocompleteFieldConfig();
  acf.id = 'mention_sources_types';
  acf.name = 'mention_sources_types[]';
  acf.type = 'mention_sources_types';
  acf.placeholder = I18n.t('Explorer.MentionSources.FilterPanel.fields.mention_sources_types.placeholder');
  return acf;
};

const FilterPanel = () => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();

  const [filterSet, setFilterSet] = useState(() => new FilterSet(params.filters));
  const [minimumDate, setMinimumDate] = useState(null);
  const [maximumDate, setMaximumDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const mentionSourcesField = useRef(null);
  const countriesField = useRef(null);
  const mentionedAfterField = useRef(null);
  const mentionedBeforeField = useRef(null);

  const clearAutocompleteFilters = (fs) => {
    if (countriesField.current && fs.get('mention_sources_countries[]').length === 0) {
      countriesField.current.clearSelected();
    }
    if (mentionSourcesField.current && fs.get('mention_sources_types[]').length === 0) {
      mentionSourcesField.current.clearSelected();
    }
  };

  useEffect(() => {
    const fs = new FilterSet(params.filters);
    setFilterSet(fs);
    clearAutocompleteFilters(fs);
  }, [JSON.stringify(params.filters)]);

  useEffect(() => {
    if (params.mentionSourceOrder === 'followers_desc' && mentionSourcesField.current) {
      mentionSourcesField.current.overrideSelectedMentionSourcesFromTypes(['tw']);
    }
  }, [params.mentionSourceOrder]);

  const typeOptions = buildTypeOptions();
  const autocompleteFieldConfig = buildAutocompleteFieldConfig();

  const areDatesValid = () => {
    if (!mentionedAfterField.current?.checkValidity() || !mentionedBeforeField.current?.checkValidity()) {
      setErrorMessage('Start date must be before end date');
      return false;
    }

    const fromDate = mentionedAfterField.current.value;
    const toDate = mentionedBeforeField.current.value;

    if (fromDate === '' && toDate === '') return true;

    if (toDate !== '' && fromDate === '') {
      setErrorMessage('Please specify a start date within 1 year of the end date');
      return false;
    }

    const fromDateObject = dayjs(fromDate);
    const toDateObject = dayjs(toDate || new Date());

    if (fromDateObject.add(1, 'year').isBefore(toDateObject)) {
      setErrorMessage('Please specify a date range within 1 year');
      return false;
    }

    return true;
  };

  const applySearch = () => {
    setErrorMessage(null);

    if (!areDatesValid()) return;

    Analytics.trackEvent('apply-mention-author-filter');

    // Ordering by followers makes sense for X (Twitter) only; reset if the
    // user removes Twitter from the source-type filter.
    let next = QueryParams.withFilters(params, filterSet.populatedFilters);
    if (
      params.mentionSourceOrder === 'followers_desc' &&
      !filterSet.get('mention_sources_types[]').includes('type:tw')
    ) {
      next = QueryParams.withMentionSourceOrder(next, QueryParams.DEFAULT_MENTION_SOURCE_ORDER);
    }

    if (QueryParams.buildSearch(next) !== window.location.search.replace('?', '')) {
      navigateParams(next);
    }
  };

  const clearSearch = () => {
    mentionSourcesField.current?.clear();
    mentionedBeforeField.current?.clear();
    mentionedAfterField.current?.clear();
    countriesField.current?.clear();
  };

  const resetSearch = () => {
    clearSearch();
    applySearch();
  };

  const trackCountriesSearch = () => {
    Analytics.trackEvent('filter-mention-author-country');
  };

  return (
    <div className="Explorer-Form Explorer-MentionSources-FilterPanel">
      <div className="Explorer-MentionSources-FilterPanel-row">
        <MentionSourcesFilter
          sourcesTypes={typeOptions}
          filterSet={filterSet}
          queryFilter="mentionSourcesTypesForFilter"
          dropdownSourcesType="mention_source_type"
          autocompleteFieldConfig={autocompleteFieldConfig}
          assignRef={(f) => (mentionSourcesField.current = f)}
        />
      </div>
      <div className="Explorer-MentionSources-FilterPanel-row">
        <div className="Explorer-MentionSources-FilterPanel-date-range">
          <div className="Explorer-FilterPanel-field">
            <DateField
              fieldID="mention_sources_mentioned_after"
              fieldName="mention_sources_mentioned_after"
              label={I18n.t('Explorer.MentionSources.FilterPanel.fields.mentioned_after.title')}
              filterSet={filterSet}
              placeholder={I18n.t('Explorer.MentionSources.FilterPanel.fields.mentioned_after.placeholder')}
              onChangeCallback={setMinimumDate}
              maximumDate={maximumDate || filterSet.get('mention_sources_mentioned_before')}
              ref={mentionedAfterField}
            />
          </div>
          <div className="Explorer-FilterPanel-field">
            <DateField
              fieldID="mention_sources_mentioned_before"
              fieldName="mention_sources_mentioned_before"
              label={I18n.t('Explorer.MentionSources.FilterPanel.fields.mentioned_before.title')}
              filterSet={filterSet}
              placeholder={I18n.t('Explorer.MentionSources.FilterPanel.fields.mentioned_before.placeholder')}
              onChangeCallback={setMaximumDate}
              minimumDate={minimumDate || filterSet.get('mention_sources_mentioned_after')}
              ref={mentionedBeforeField}
            />
          </div>
        </div>
        <CountriesField
          filterSet={filterSet}
          fieldID="mention_sources_countries"
          fieldName="mention_sources_countries[]"
          filterSetParam="mention_sources_countries[]"
          label={I18n.t('Explorer.MentionSources.FilterPanel.fields.countries.label')}
          placeholder={I18n.t('Explorer.Form.CountriesField.placeholder')}
          onChangeCallback={trackCountriesSearch}
          ref={countriesField}
        />
        <div className="Explorer-FilterPanel-buttons">
          <button className="apply-button" onClick={applySearch}>
            {I18n.t('Explorer.MentionSources.FilterPanel.buttons.apply')}
          </button>
          <button className="clear-button" onClick={resetSearch}>
            {I18n.t('Explorer.MentionSources.FilterPanel.buttons.reset')}
          </button>
        </div>
      </div>
      {errorMessage && <div className="Explorer-FilterPanel-warning-message">{errorMessage}</div>}
    </div>
  );
};

export default FilterPanel;
